import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCellarStore } from '../store/CellarStoreContext';
import { drinkBottle, wineStock, type Wine } from '../domain/cellar';
import { winesToDrink } from '../domain/drink-search';
import { BackButton } from '../components/BackButton';
import { NumberStepper } from '../components/NumberStepper';
import { RatingStepper } from '../components/RatingStepper';
import { bottleCount, t } from '../i18n';
import './DrinkPage.css';

/**
 * /c/:cellarId/boire, "Boire une bouteille" (owner requests, 2026-10-06; formerly "Réduire le
 * stock", then "Boire et noter" 2026-10-09, renamed 2026-10-11): a search on the Domaine and the
 * Parcelle lists the wines with bottles left as simple cards (Domaine, vintage, bottles left); choosing one shows the bottles drunk (1 by
 * default, up to the bottles left; owner 2026-10-09), the rating, 5 by default with up and down
 * arrows, and a comment. "Valider" records them in one tasting (FR-012) and goes back to the
 * cellar's home page, which says so.
 */
export function DrinkPage() {
  const { cellarId } = useParams<{ cellarId: string }>();
  const navigate = useNavigate();
  const { cellar, apply } = useCellarStore();
  const [query, setQuery] = useState('');
  const [chosenId, setChosenId] = useState<string | undefined>();
  const [bottles, setBottles] = useState(1);
  const [rating, setRating] = useState<number | undefined>(5);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | undefined>();
  const found = useMemo(() => winesToDrink(cellar?.wines ?? [], query), [cellar, query]);
  const chosen = cellar?.wines.find((wine) => wine.id === chosenId);
  const home = `/c/${cellarId}`;

  if (!cellar) {
    return (
      <main>
        <p>{t('common.loading')}</p>
      </main>
    );
  }

  function choose(wine: Wine) {
    setChosenId(wine.id);
    setBottles(1);
    setRating(5);
    setNotes('');
  }

  function confirm(event: FormEvent) {
    event.preventDefault();
    if (!chosen) return;
    setError(undefined);
    try {
      apply(
        drinkBottle({
          wineId: chosen.id,
          bottles,
          ...(rating !== undefined ? { rating } : {}),
          ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
        }),
      ).catch(() => setError(t('error.saveFailed')));
      navigate(home, { state: { drunk: `${chosen.domaine} ${chosen.annee}`, bottles } });
    } catch {
      setError(t('error.saveFailed'));
    }
  }

  return (
    <main className="drink-page">
      <header className="page-title">
        <BackButton home={home} />
        <h1>{t('home.reduce')}</h1>
      </header>
      {error && <p role="alert">{error}</p>}

      {chosen ? (
        <form className="drink-page__form" onSubmit={confirm} aria-label={t('drink.title')}>
          <WineResult wine={chosen} />
          <NumberStepper
            label={t('drink.bottles')}
            value={bottles}
            max={wineStock(chosen)}
            decreaseLabel={t('drink.bottlesLess')}
            increaseLabel={t('drink.bottlesMore')}
            onChange={setBottles}
          />
          <RatingStepper label={t('drink.rating')} value={rating} onChange={setRating} />
          <label htmlFor="drink-notes">{t('drink.notes')}</label>
          <textarea
            id="drink-notes"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
          <div className="drink-form__actions">
            <button type="button" onClick={() => setChosenId(undefined)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="drink-form__confirm">
              {t('drink.confirm')}
            </button>
          </div>
        </form>
      ) : (
        <>
          <label htmlFor="drink-search">{t('drink.search')}</label>
          <input
            id="drink-search"
            type="search"
            placeholder={t('drink.searchPlaceholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {found.length === 0 ? (
            <p>{t('drink.noMatch')}</p>
          ) : (
            <ul className="drink-page__results">
              {found.map((wine) => (
                <li key={wine.id}>
                  <button
                    type="button"
                    className={`drink-result wine-card--${wine.typeDeVin}`}
                    onClick={() => choose(wine)}
                  >
                    <WineResult wine={wine} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}

/** A simple wine card: Domaine (and Parcelle), vintage, bottles left. */
function WineResult({ wine }: { wine: Wine }) {
  return (
    <span className="drink-result__body">
      <span className="drink-result__name">
        {wine.domaine}
        {wine.parcelle ? ` — ${wine.parcelle}` : ''} {wine.annee}
      </span>
      <span className="drink-result__stock">{bottleCount(wineStock(wine))}</span>
    </span>
  );
}
